import flatten from 'lodash/flatten';
import React, { memo, useEffect, useMemo, useRef } from 'react';
import {
    useAppMode,
    useConversation,
    useEventSubscription,
    useSelectedComment,
    useSetTicket,
    useTicket,
    useTicketEventsQuery,
} from '@tymely/services';
import { pdfjs } from 'react-pdf';
import { useApi } from '@tymely/api';
import { ITicket, ITicketTrailEvent } from '@tymely/atoms';
import { LlmTrailEvent } from '@tymely/atoms/llmTrail.atoms';
import { useLlmTrailEventsQuery } from '@tymely/services/hooks/llmTrail.services';

import { POLICY_TYPES, TICKET_OPERATIONS } from '../../constants';
import { useEventMode } from '../../EventModeContext';
import { Response } from '../Response';
import { BottomScrollButton, UpScrollButton } from './ScrollButton';
import Discussion from './Discussion';
import { ConversationContainer } from './styled';

import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-photo-view/dist/react-photo-view.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

type ConversationProps = {
    highlights?: [number, number][];
    highlightText?: string;
    canSave?: boolean;
    canReview?: boolean;
    canSubmit?: boolean;
};

const useEventData = (ticketId: number) => {
    const { eventMode } = useEventMode();
    const {
        data: ticketEventsData,
        isLoading: isLoadingTicketEvents,
        isFetchingNextPage: isFetchingNextPageTicketEvents,
        fetchNextPage: fetchNextPageTicketEvents,
        hasNextPage: hasNextPageTicketEvents,
    } = useTicketEventsQuery(ticketId, eventMode === 'ticketEventsMode' || eventMode === 'chatEventsMode');

    const {
        data: llmEventsData,
        isLoading: isLoadingLlmEvents,
        isFetchingNextPage: isFetchingNextPageLlmEvents,
        fetchNextPage: fetchNextPageLlmEvents,
        hasNextPage: hasNextPageLlmEvents,
    } = useLlmTrailEventsQuery(ticketId, eventMode === 'llmMode');

    const isLoading = isLoadingTicketEvents || isLoadingLlmEvents;
    const isFetchingNextPage = isFetchingNextPageTicketEvents || isFetchingNextPageLlmEvents;
    const hasNextPage = hasNextPageTicketEvents || hasNextPageLlmEvents;

    const fetchNextPage = () => {
        if (eventMode === 'ticketEventsMode' || (eventMode === 'chatEventsMode' && hasNextPageTicketEvents)) {
            fetchNextPageTicketEvents();
        } else if (eventMode === 'llmMode' && hasNextPageLlmEvents) {
            fetchNextPageLlmEvents();
        }
    };

    const events = useMemo(() => {
        if (eventMode === 'ticketEventsMode') {
            return flatten(ticketEventsData?.pages || []) as ITicketTrailEvent[];
        }
        if (eventMode === 'chatEventsMode') {
            const allEvents = flatten(ticketEventsData?.pages || []) as ITicketTrailEvent[];
            return allEvents.filter(
                (event) =>
                    !event.additional_data?.audit_log?.simulation_id &&
                    !event.additional_data?.decision?.simulation_id &&
                    (event.operation === TICKET_OPERATIONS.CHAT_CONVERSER ||
                        event.operation === TICKET_OPERATIONS.USER_PROMPT ||
                        event.operation === TICKET_OPERATIONS.LLM_RESPONSE ||
                        event.operation === TICKET_OPERATIONS.LLM_TRIGGER ||
                        (event.operation === TICKET_OPERATIONS.END_EVALUATING_POLICY &&
                            (event.additional_data?.decision?.policy_type === POLICY_TYPES.PRE_FILTER ||
                                (event.additional_data?.decision?.policy_type === POLICY_TYPES.FOLLOW_UP &&
                                    event.additional_data?.decision?.title !== 'No decision')))),
            );
        }
        if (eventMode === 'llmMode') {
            return flatten(llmEventsData?.pages || []) as LlmTrailEvent[];
        }
        return [];
    }, [eventMode, ticketEventsData, llmEventsData]);

    return {
        events,
        isLoading,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
    };
};
const Conversation = memo(({ highlightText, ...rest }: ConversationProps) => {
    const chatRef = useRef<{
        scrollToTop: () => void;
        scrollToBottom: () => void;
    }>(null);

    const { isOnline } = useAppMode();
    const ticket = useTicket();

    const { events, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } = useEventData(ticket.id);

    useEffect(() => {
        if (hasNextPage) {
            fetchNextPage();
        }
    }, [events, hasNextPage]);

    const api = useApi();
    const setTicket = useSetTicket();

    const { comments, loaded, refetch, setCommentIdToFocusOn } = useConversation();

    useEventSubscription(
        {
            topic: `ticket/${ticket?.id}`,
            filter: (event) => ['Comment', 'Ticket'].includes(event.object_type),
            enabled: !!ticket,
        },
        async (event) => {
            if (event.object_type === 'Ticket') {
                const ticketUpdated = (await api.get(`ticket/${ticket.id}`)) as ITicket;
                setTicket(ticketUpdated);
            } else if (event.object_type === 'Comment' && event.action === 'created') {
                await refetch();
                chatRef.current?.scrollToBottom();
            }
        },
    );

    const selectedComment = useSelectedComment();

    return (
        <ConversationContainer id="conversation">
            <UpScrollButton onClick={chatRef.current?.scrollToTop} />
            <Discussion
                highlightText={highlightText}
                ref={chatRef}
                ticket={ticket}
                comments={comments}
                commentIdToFocusOn={selectedComment?.id}
                selectedCommentId={selectedComment?.id}
                loading={!loaded}
                onSelectComment={!isOnline ? setCommentIdToFocusOn : undefined}
                onDeselectComment={() => setCommentIdToFocusOn(undefined)}
                events={{
                    isLoading: isLoading || isFetchingNextPage,
                    data: events,
                }}
            />
            <BottomScrollButton onClick={chatRef.current?.scrollToBottom} />
            <Response {...rest} scrollToBottom={() => chatRef.current?.scrollToBottom?.()} />
        </ConversationContainer>
    );
});

Conversation.displayName = 'Conversation';

export default Conversation;
